import { useState, useCallback, useEffect } from 'react';

const TOUR_STORAGE_KEY = 'chesshatch_onboarding_completed';
const TOUR_SKIPPED_KEY = 'chesshatch_onboarding_skipped';

export interface TourStep {
  target: string;
  title: string;
  content: string;
  placement: 'top' | 'bottom' | 'left' | 'right';
}

export const tourSteps: TourStep[] = [
  {
    target: '[data-tour="play-button"]',
    title: "🎮 Start Playing!",
    content: "Click here to challenge our AI opponents. They're friendly and great for practice!",
    placement: "bottom"
  },
  {
    target: '[data-tour="ai-squad"]',
    title: "🤖 Challenge AI Squad",
    content: "Meet our AI Challengers! They come in all skill levels, from beginner to grandmaster.",
    placement: "bottom"
  },
  {
    target: '[data-tour="hatch-evolution"]',
    title: "🧬 Hatch Evolution",
    content: "Try our unique Hatch mode! Capture pieces to evolve your pawns into stronger pieces.",
    placement: "bottom"
  },
  {
    target: '[data-tour="leaderboard"]',
    title: "🏆 Leaderboards",
    content: "See how you rank against other players. Climb to the top!",
    placement: "bottom"
  },
  {
    target: '[data-tour="profile"]',
    title: "👤 Your Profile",
    content: "Track your progress, customize your avatar, and view your game stats here!",
    placement: "left"
  }
];

export function useOnboardingTour() {
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [hasCompleted, setHasCompleted] = useState(false);
  const [hasSkipped, setHasSkipped] = useState(false);

  // Check localStorage on mount
  useEffect(() => {
    const completed = localStorage.getItem(TOUR_STORAGE_KEY) === 'true';
    const skipped = localStorage.getItem(TOUR_SKIPPED_KEY) === 'true';
    setHasCompleted(completed);
    setHasSkipped(skipped);
  }, []);

  const startTour = useCallback(() => {
    setCurrentStep(0);
    setIsActive(true);
  }, []);

  const nextStep = useCallback(() => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Tour completed
      setIsActive(false);
      setHasCompleted(true);
      localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    }
  }, [currentStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const skipTour = useCallback(() => {
    setIsActive(false);
    setHasSkipped(true);
    localStorage.setItem(TOUR_SKIPPED_KEY, 'true');
  }, []);

  const endTour = useCallback(() => {
    setIsActive(false);
    setHasCompleted(true);
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    localStorage.removeItem(TOUR_SKIPPED_KEY);
    setHasCompleted(false);
    setHasSkipped(false);
    setCurrentStep(0);
  }, []);

  const shouldShowTour = !hasCompleted && !hasSkipped;

  return {
    isActive,
    currentStep,
    currentStepData: tourSteps[currentStep],
    totalSteps: tourSteps.length,
    hasCompleted,
    hasSkipped,
    shouldShowTour,
    startTour,
    nextStep,
    prevStep,
    skipTour,
    endTour,
    resetTour
  };
}
